import type { FC } from 'react';
import { useTranslation } from 'react-i18next';
import { Typography } from '@mui/material';
import dayjs, { Dayjs } from 'dayjs';

import { useCountdownTimer } from '@mopla/business-logic';
import { addLeadingZeroNum } from '@mopla/utils';

interface IMplDeutschlandTicketRemainingTimeLeftProps {
	endDate: Dayjs;
	isPreordered: boolean;
}
type TTimerOptions = {
	days: string;
	hours: string | number;
	minutes: string | number;
	seconds: string | number;
	months?: string | number;
};

export const MplDeutschlandTicketRemainingTimeLeft: FC<
	IMplDeutschlandTicketRemainingTimeLeftProps
> = ({ endDate, isPreordered }) => {
	const { t } = useTranslation('userDTicket');
	const { timerString } = useCountdownTimer(endDate.toDate());

	const preorderedTimerText =
		(timerString?.months || 0) > 0
			? 'ticket_start_timer_with_months'
			: 'ticket_start_timer';

	const timerText = isPreordered
		? `dticket_large.timer.${preorderedTimerText}`
		: 'dticket_large.timer.ticket_duration_timer';

	// Under a day left → use the "_no_days" template variant so the label
	// doesn't read "0 Tage" / "0 days".
	const hideDays = (timerString.days || 0) === 0;
	const timerKey = hideDays ? `${timerText}_no_days` : timerText;

	// get diff in days and set this value to display days amount without months
	const daysDiff = dayjs(endDate).diff(dayjs(), 'days');
	const months = timerString.months || 0;
	const days = months > 0 ? daysDiff : timerString.days || 0;

	const timerOptions: TTimerOptions = {
		days: t('dticket_large.timer.timer_days_text', {
			count: days,
		}),
		hours: addLeadingZeroNum(timerString.hours || 0),
		minutes: addLeadingZeroNum(timerString.minutes || 0),
		seconds: addLeadingZeroNum(timerString.seconds || 0),
	};

	if (isPreordered) {
		timerOptions.months = t('dticket_large.timer.timer_months_text', {
			count: months,
		});
		timerOptions.days = t('dticket_large.timer.timer_days_text', {
			count: timerString.days || 0,
		});
	}

	return (
		<Typography
			variant="l1"
			color="solutions.600"
			data-testid="dticket-validity-remaining-time"
		>
			{t(timerKey, timerOptions)}
		</Typography>
	);
};
